import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../hooks/useTheme';

interface AdSlotProps {
  adContent?: {
    title: string;
    subtitle: string;
    ctaText: string;
    imageUrl?: string;
    backgroundColor?: string;
    accentColor?: string;
    discount?: string;
    timeLimit?: string;
    socialProof?: string;
    progress?: number;
  };
  position?: 'top' | 'middle' | 'bottom';
  variant?: 'banner' | 'card' | 'featured' | 'compact';
  onAdPress?: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
  showProgress?: boolean;
  showCountdown?: boolean;
  showSocialProof?: boolean;
  autoDismiss?: number;
}

const AdSlot: React.FC<AdSlotProps> = ({
  adContent,
  position = 'middle',
  variant = 'card',
  onAdPress,
  onClose,
  showCloseButton = false,
  showProgress = false,
  showCountdown = false,
  showSocialProof = false,
  autoDismiss,
}) => {
  const { colors } = useTheme();
  const [dismissed, setDismissed] = useState(false);
  const [countdown, setCountdown] = useState(30);
  const fadeAnim = useState(new Animated.Value(1))[0];

  // Default ad content if none provided
  const defaultAdContent = {
    title: 'Upgrade to Premium',
    subtitle:
      'Unlock unlimited color analysis and personalized recommendations',
    ctaText: 'Get Premium',
    backgroundColor: '#6366F1',
    accentColor: '#FFFFFF',
    discount: '50% OFF',
    timeLimit: 'Limited Time',
    socialProof: '2,847 users upgraded this week',
    progress: 75,
  };

  const content = adContent || defaultAdContent;

  const handleDismiss = useCallback(() => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start(() => {
      setDismissed(true);
      onClose?.();
    });
  }, [fadeAnim, onClose]);

  // Auto-dismiss functionality
  useEffect(() => {
    if (autoDismiss && !dismissed) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoDismiss);
      return () => clearTimeout(timer);
    }
  }, [autoDismiss, dismissed, handleDismiss]);

  // Countdown timer
  useEffect(() => {
    if (showCountdown && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showCountdown, countdown]);

  if (dismissed) return null;

  // Different ad configurations based on variant
  const getAdStyles = () => {
    switch (variant) {
      case 'banner':
        return {
          container: styles.bannerContainer,
          content: styles.bannerContent,
          title: styles.bannerTitle,
          subtitle: styles.bannerSubtitle,
          cta: styles.bannerCta,
        };
      case 'compact':
        return {
          container: styles.compactContainer,
          content: styles.compactContent,
          title: styles.compactTitle,
          subtitle: styles.compactSubtitle,
          cta: styles.compactCta,
        };
      case 'featured':
        return {
          container: styles.featuredContainer,
          content: styles.featuredContent,
          title: styles.featuredTitle,
          subtitle: styles.featuredSubtitle,
          cta: styles.featuredCta,
        };
      default: // card
        return {
          container: styles.cardContainer,
          content: styles.cardContent,
          title: styles.cardTitle,
          subtitle: styles.cardSubtitle,
          cta: styles.cardCta,
        };
    }
  };

  const adStyles = getAdStyles();

  // Position-specific margins
  const getPositionMargin = () => {
    switch (position) {
      case 'top':
        return { marginBottom: 16 };
      case 'bottom':
        return { marginTop: 16 };
      default:
        return { marginVertical: 12 };
    }
  };

  return (
    <Animated.View
      style={[adStyles.container, getPositionMargin(), { opacity: fadeAnim }]}
    >
      <TouchableOpacity
        style={[
          adStyles.content,
          {
            backgroundColor: content.backgroundColor || colors.card,
            borderColor: colors.border,
          },
        ]}
        onPress={onAdPress}
        activeOpacity={0.8}
      >
        {/* Close button for dismissible ads */}
        {showCloseButton && onClose && (
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleDismiss}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons
              name="close"
              size={16}
              color={content.accentColor || colors.secondary}
            />
          </TouchableOpacity>
        )}

        {/* Ad badge with optional countdown */}
        <View
          style={[
            styles.adBadge,
            { backgroundColor: content.accentColor + '20' },
          ]}
        >
          <Text
            style={[
              styles.adBadgeText,
              { color: content.accentColor || colors.primary },
            ]}
          >
            {showCountdown ? `${countdown}s` : 'Ad'}
          </Text>
        </View>

        {/* Discount badge */}
        {content.discount && (
          <View style={[styles.discountBadge, { backgroundColor: '#FF6B6B' }]}>
            <Text style={[styles.discountText, { color: '#FFFFFF' }]}>
              {content.discount}
            </Text>
          </View>
        )}

        {/* Main content */}
        <View style={styles.adMainContent}>
          <View style={styles.adTextContent}>
            <Text
              style={[
                adStyles.title,
                { color: content.accentColor || colors.text },
              ]}
            >
              {content.title}
            </Text>
            <Text
              style={[
                adStyles.subtitle,
                { color: content.accentColor + 'CC' || colors.secondary },
              ]}
            >
              {content.subtitle}
            </Text>

            {/* Social proof */}
            {showSocialProof && content.socialProof && (
              <View style={styles.socialProofContainer}>
                <Ionicons
                  name="people"
                  size={12}
                  color={content.accentColor + '80' || colors.secondary}
                />
                <Text
                  style={[
                    styles.socialProofText,
                    { color: content.accentColor + '80' || colors.secondary },
                  ]}
                >
                  {content.socialProof}
                </Text>
              </View>
            )}

            {/* Progress bar */}
            {showProgress && content.progress && (
              <View style={styles.progressContainer}>
                <View
                  style={[
                    styles.progressBar,
                    { backgroundColor: content.accentColor + '30' },
                  ]}
                >
                  <View
                    style={[
                      styles.progressFill,
                      {
                        backgroundColor: content.accentColor || colors.primary,
                        width: `${content.progress}%`,
                      },
                    ]}
                  />
                </View>
                <Text
                  style={[
                    styles.progressText,
                    { color: content.accentColor || colors.primary },
                  ]}
                >
                  {content.progress}% complete
                </Text>
              </View>
            )}
          </View>

          {/* CTA Button */}
          <TouchableOpacity
            style={[
              adStyles.cta,
              {
                backgroundColor: content.accentColor || colors.primary,
              },
            ]}
            onPress={onAdPress}
            activeOpacity={0.7}
          >
            <Text
              style={[
                styles.ctaText,
                { color: content.backgroundColor || '#FFFFFF' },
              ]}
            >
              {content.ctaText}
            </Text>
            <Ionicons
              name="arrow-forward"
              size={14}
              color={content.backgroundColor || '#FFFFFF'}
            />
          </TouchableOpacity>
        </View>

        {/* Decorative elements */}
        <View
          style={[
            styles.decorativeIcon,
            { backgroundColor: content.accentColor + '15' },
          ]}
        >
          <Ionicons
            name="sparkles"
            size={20}
            color={content.accentColor || colors.primary}
          />
        </View>

        {/* Time limit indicator */}
        {content.timeLimit && (
          <View
            style={[
              styles.timeLimitBadge,
              { backgroundColor: content.accentColor + '20' },
            ]}
          >
            <Ionicons
              name="time-outline"
              size={12}
              color={content.accentColor || colors.primary}
            />
            <Text
              style={[
                styles.timeLimitText,
                { color: content.accentColor || colors.primary },
              ]}
            >
              {content.timeLimit}
            </Text>
          </View>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  // Banner variant (horizontal, compact)
  bannerContainer: {
    width: '100%',
  },
  bannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    position: 'relative',
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  bannerSubtitle: {
    fontSize: 12,
    fontWeight: '400',
    opacity: 0.8,
  },
  bannerCta: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 'auto',
  },

  // Compact variant (minimal)
  compactContainer: {
    width: '100%',
  },
  compactContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderRadius: 6,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    position: 'relative',
  },
  compactTitle: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 1,
  },
  compactSubtitle: {
    fontSize: 11,
    fontWeight: '400',
    opacity: 0.7,
  },
  compactCta: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginLeft: 'auto',
  },

  // Card variant (vertical, detailed)
  cardContainer: {
    width: '100%',
  },
  cardContent: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    position: 'relative',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 6,
  },
  cardSubtitle: {
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 18,
    marginBottom: 12,
  },
  cardCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 10,
    alignSelf: 'flex-start',
  },

  // Featured variant (large, prominent)
  featuredContainer: {
    width: '100%',
  },
  featuredContent: {
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 16,
    elevation: 8,
    position: 'relative',
    minHeight: 140,
  },
  featuredTitle: {
    fontSize: 20,
    fontWeight: '800',
    marginBottom: 10,
  },
  featuredSubtitle: {
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
    marginBottom: 16,
  },
  featuredCta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderRadius: 14,
    alignSelf: 'flex-start',
  },

  // Common styles
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 10,
    padding: 4,
  },
  adBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
  },
  adBadgeText: {
    fontSize: 9,
    fontWeight: '600',
  },
  discountBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  discountText: {
    fontSize: 10,
    fontWeight: '700',
  },
  adMainContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 28,
  },
  adTextContent: {
    flex: 1,
    marginRight: 12,
  },
  ctaText: {
    fontSize: 13,
    fontWeight: '600',
    marginRight: 4,
  },
  decorativeIcon: {
    position: 'absolute',
    bottom: 12,
    right: 12,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
  },
  socialProofContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
    gap: 4,
  },
  socialProofText: {
    fontSize: 10,
    fontWeight: '500',
  },
  progressContainer: {
    marginTop: 8,
  },
  progressBar: {
    height: 4,
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 4,
  },
  progressFill: {
    height: '100%',
    borderRadius: 2,
  },
  progressText: {
    fontSize: 10,
    fontWeight: '600',
  },
  timeLimitBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 3,
    borderRadius: 6,
    gap: 3,
  },
  timeLimitText: {
    fontSize: 9,
    fontWeight: '600',
  },
});

export default AdSlot;
